import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link, useNavigate, useRouterState } from "@tanstack/react-router"
import { ChevronRight } from "lucide-react"

import { unreadMessageCount, useChat } from "@/features/chat"
import { advisorsList, zodiacIcons, type Advisor } from "@/features/advisors"
import {
  OnboardingReportLockModal,
  useOnboardingLock,
} from "@/features/onboarding-lock"
import { cn } from "@/lib/utils"
import { getParamsFromStorage } from "@/shared/lib/storage-params"
import { preloadJson } from "@/shared/lib/cached-json"

import { WebappRoutes, type WebappRoute } from "../config/routes"
import { useWebApp } from "../hooks/use-webapp"
import { usePalmistryContext } from "../hooks/use-palmistry"
import { useUserZodiac } from "../hooks/use-user-zodiac"
import { ChatWithAdvisorModal } from "./chat-with-advisor-modal"
import { PalmReportModal } from "./palm-report-modal"

import Advisors from "@/assets/svg/lpr4/navigation/advisors.svg?react"
import Chats from "@/assets/svg/lpr4/navigation/chats.svg?react"
import Comp from "@/assets/svg/lpr4/navigation/comp.svg?react"
import Palm from "@/assets/svg/lpr4/navigation/palmistry.svg?react"
import Border from "@/assets/svg/lpr4/navigation/navigation-border.svg?react"

interface Tab {
  label: string
  href: WebappRoute
  icon?: React.ReactNode
}

const tabsList: Tab[] = [
  { label: "Advisors", href: WebappRoutes.ADVISORS, icon: <Advisors /> },
  { label: "Readings", href: WebappRoutes.READINGS, icon: <Chats /> },
  { label: "Today", href: WebappRoutes.TODAY },
  { label: "Compatibility", href: WebappRoutes.COMPATIBILITY, icon: <Comp /> },
  { label: "Palmistry", href: WebappRoutes.PALMISTRY, icon: <Palm /> },
]

export function WebappNav() {
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const zodiac = useUserZodiac()
  const { timeLeft } = usePalmistryContext()
  const { remainingLabel: onboardingTimeLeft, isLocked } = useOnboardingLock()
  const onboardingPalmLocked = isLocked("palmistry_tab")
  const onboardingCompatLocked = isLocked("compatibility_tab")
  const [onboardingLockModalOpen, setOnboardingLockModalOpen] = useState(false)
  const { hasNewMarkers, setBottomInset } = useWebApp()
  const {
    threads,
    unreadAdvisorIds,
    unreadAdvisors,
    setUnreadAdvisors,
    setSelectedAdvisor,
    userCreditsCount,
    isPalmReadingLocked,
    palmReportModalOpen,
    setPalmReportModalOpen,
    chatWithAdvisorModalOpen,
    setChatWithAdvisorModalOpen,
  } = useChat()

  const { redirectToAdvisor } = getParamsFromStorage(["redirectToAdvisor"])

  const unreadMessageCountTotal = unreadMessageCount(unreadAdvisorIds)
  const unreadAdvisor = useMemo<Advisor | null>(() => {
    if (!unreadAdvisors || unreadAdvisors.length === 0) return null
    const advisor = advisorsList.find((a) => unreadAdvisors[0].id === a.assistantId)
    return advisor ? { ...advisor, message: unreadAdvisors[0].message } : null
  }, [unreadAdvisors])

  // The banner floats above the nav (absolute), so the routed content needs extra
  // bottom padding = its height, or it covers the page's bottom rows. Measure it
  // (ResizeObserver handles a 1- vs 2-line message) and hand the height to the
  // shell; reset to 0 when the banner isn't shown.
  const bannerRef = useRef<HTMLButtonElement | null>(null)
  useEffect(() => {
    if (!unreadAdvisor) {
      setBottomInset(0)
      return
    }
    const el = bannerRef.current
    if (!el) return
    const measure = () => setBottomInset(el.offsetHeight)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => {
      observer.disconnect()
      setBottomInset(0)
    }
  }, [unreadAdvisor, setBottomInset])

  useEffect(() => {
    // Temporarily disabled: the "Personal Gift" popup is hidden for now.
    // Re-enable by uncommenting the setter below.
    if (
      threads.length > 0 &&
      !redirectToAdvisor &&
      userCreditsCount! > 120 &&
      userCreditsCount! <= 180
    ) {
      // setChatWithAdvisorModalOpen(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threads])

  const hasNew = useMemo(() => Object.values(hasNewMarkers).some(Boolean), [hasNewMarkers])

  const goToReadings = useCallback(() => {
    navigate({ to: WebappRoutes.READINGS })
  }, [navigate])

  const handleUnreadClick = useCallback(
    (advisor: Advisor) => {
      setSelectedAdvisor(advisor)
      setUnreadAdvisors((prev) => prev.filter((u) => u.id !== advisor.assistantId))
      goToReadings()
    },
    [setSelectedAdvisor, setUnreadAdvisors, goToReadings],
  )

  // Prefer the in-app palmistry 10‑min badge when active; else show onboarding 30‑min.
  const palmistryBadge =
    timeLeft !== null ? timeLeft : onboardingPalmLocked ? onboardingTimeLeft : null

  return (
    <div className="fixed bottom-0 left-1/2 z-[99] flex w-full max-w-app -translate-x-1/2 flex-col">
      <div className="relative -bottom-px z-[6] flex items-center justify-end [&>svg]:w-full">
        <Border />
      </div>

      {unreadAdvisor && (
        <button
          ref={bannerRef}
          type="button"
          onClick={() => handleUnreadClick(unreadAdvisor)}
          className="absolute bottom-16 flex w-full items-center justify-between gap-3.5 rounded-t-2xl border-t border-purple-light/10 bg-dark-surface p-3 text-left"
        >
          <span className="absolute -top-3 right-3.5 flex items-center gap-1.5 rounded-2xl border-2 border-dark-base bg-peach px-2 py-1 font-body text-[10px] font-semibold uppercase text-white">
            <span className="flex min-w-4 items-center justify-center rounded-full bg-white/20 px-1">
              {unreadMessageCountTotal}
            </span>
            {unreadMessageCountTotal === 1 ? "New message received" : "New messages received"}
          </span>
          <span className="relative shrink-0">
            <img
              src={unreadAdvisor.photo}
              alt={unreadAdvisor.name}
              className="size-14 rounded-full border-2 border-main-purple/40 object-cover object-top"
            />
            <span className="absolute bottom-0.5 right-0.5 size-3 rounded-full border-2 border-dark-surface bg-green" />
          </span>
          <span className="flex-1">
            <span className="block font-body text-base font-medium text-purple-light">
              {unreadAdvisor.name}
            </span>
            <span className="line-clamp-2 font-body text-sm text-purple-light/60">
              {unreadAdvisor.message}
            </span>
          </span>
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-purple-light/10">
            <ChevronRight className="size-5 text-purple-light" />
          </span>
        </button>
      )}

      <nav className="relative m-0 flex min-h-16 w-full items-end justify-evenly bg-dark-base px-0 pb-1 pt-1.5">
        {tabsList.map((tab) => {
          const isActive = pathname === tab.href
          const isToday = tab.href === WebappRoutes.TODAY
          const isReadings = tab.href === WebappRoutes.READINGS
          const isPalmistry = tab.href === WebappRoutes.PALMISTRY
          const isCompatibility = tab.href === WebappRoutes.COMPATIBILITY
          const color = isActive ? "text-main-purple" : "text-purple-light/40"

          const handleClick = (e: React.MouseEvent) => {
            if (isPalmistry && isPalmReadingLocked) {
              e.preventDefault()
              setPalmReportModalOpen(true)
            } else if (isPalmistry && onboardingPalmLocked) {
              e.preventDefault()
              setOnboardingLockModalOpen(true)
            } else if (isCompatibility && onboardingCompatLocked) {
              e.preventDefault()
              setOnboardingLockModalOpen(true)
            } else if (isReadings) {
              setSelectedAdvisor(null)
            }
          }

          return (
            <Link
              key={tab.href}
              to={tab.href}
              onClick={handleClick}
              onPointerEnter={
                isCompatibility ? () => preloadJson("/json/compatibility.json") : undefined
              }
              onPointerDown={
                isCompatibility ? () => preloadJson("/json/compatibility.json") : undefined
              }
              className={cn(
                "relative flex flex-1 flex-col items-center justify-center font-body text-xs font-medium",
                color,
                "[&>svg]:mb-[3px] [&>svg]:size-9",
              )}
            >
              {isReadings && unreadMessageCountTotal > 0 && (
                <span className="absolute bottom-1/4 right-1/4 flex size-5 items-center justify-center rounded-full border-2 border-dark-base bg-peach font-body text-[9px] font-bold text-white">
                  {unreadMessageCountTotal}
                </span>
              )}

              {isPalmistry && palmistryBadge !== null && (
                <span className="absolute bottom-3.5 left-1/2 flex w-fit -translate-x-1/2 items-center justify-center rounded-[10px] bg-[#615673] px-1.5 py-0.5 font-body text-[10px] font-semibold text-white">
                  {palmistryBadge}
                </span>
              )}

              {isCompatibility && onboardingCompatLocked && onboardingTimeLeft !== null && (
                <span className="absolute bottom-3.5 left-1/2 flex w-fit -translate-x-1/2 items-center justify-center rounded-[10px] bg-[#615673] px-1.5 py-0.5 font-body text-[10px] font-semibold text-white">
                  {onboardingTimeLeft}
                </span>
              )}

              {!isToday && tab.icon}

              {isToday && (
                <span
                  className={cn(
                    "absolute bottom-[18px] z-[8] mb-1 flex h-10 w-16 items-center justify-center rounded-[20px] bg-[#221b31] shadow-[0_0_0_5px_#0d081c] [&_svg]:size-9",
                    color,
                  )}
                >
                  {(() => {
                    const ZodiacIcon = zodiacIcons[zodiac]
                    return <ZodiacIcon />
                  })()}
                  {hasNew && (
                    <span className="absolute -bottom-[7px] left-1/2 inline-flex h-3.5 -translate-x-1/2 items-center justify-center rounded-[10px] border-2 border-dark-base bg-peach px-1.5 font-body text-[8px] font-bold text-white">
                      NEW
                    </span>
                  )}
                </span>
              )}

              {tab.label}
            </Link>
          )
        })}
      </nav>

      <PalmReportModal
        isOpen={palmReportModalOpen}
        timeLeft={timeLeft}
        onClose={() => setPalmReportModalOpen(false)}
      />
      <OnboardingReportLockModal
        isOpen={onboardingLockModalOpen}
        timeLeft={onboardingTimeLeft}
        onClose={() => setOnboardingLockModalOpen(false)}
      />
      {chatWithAdvisorModalOpen && (
        <ChatWithAdvisorModal
          onClose={() => setChatWithAdvisorModalOpen(false)}
          onNavigateToReadings={goToReadings}
        />
      )}
    </div>
  )
}
